import {
  measureItineraryPricesPerformance,
  measureTripOverviewPagePerformance,
  measureHotelSelectionPagePerformance,
  measureHotelDetailsPagePerformance,
  measureActivitySelectionPagePerformance,
  measureActivityDetailsPagePerformance,
  measureFlightSelectionPagePerformance,
  measureFerrySelectionPagePerformance,
  measureInitializationPerformance,
  measureBuildYourTripPagePerformance,
} from '@libs/utils';

import { isCrawlerUserAgent } from '../statsig/crawlerCheck';

import { getMetrics } from './metrics';

const enum PerformanceMetricType {
  PerformancePageLoaded = 'performance_page_loaded',
  PerformanceAppInitiated = 'performance_app_initiated',

  PerformanceItineraryPricesRequested = 'performance_itinerary_prices_requested',
  PerformanceItineraryPricesReceived = 'performance_itinerary_prices_received',
  PerformanceItineraryPricesRendered = 'performance_itinerary_prices_rendered',

  PerformanceBuildYourTripPageRendered = 'performance_build_your_trip_page_rendered',

  PerformanceTripOverviewPageRendered = 'performance_trip_overview_page_rendered',
  PerformanceTripOverviewPageRenderedWithContent = 'performance_trip_overview_page_rendered_with_content',

  PerformanceHotelSelectionPageRendered = 'performance_hotel_selection_page_rendered',
  PerformanceHotelSelectionPageRenderedWithContent = 'performance_hotel_selection_page_rendered_with_content',

  PerformanceHotelDetailsPageRendered = 'performance_hotel_details_page_rendered',
  PerformanceHotelDetailsPageRenderedWithContent = 'performance_hotel_details_page_rendered_with_content',

  PerformanceActivitySelectionPageRendered = 'performance_activity_selection_page_rendered',
  PerformanceActivitySelectionPageRenderedWithContent = 'performance_activity_selection_page_rendered_with_content',

  PerformanceActivityDetailsPageRendered = 'performance_activity_details_page_rendered',
  PerformanceActivityDetailsPageRenderedWithContent = 'performance_activity_details_page_rendered_with_content',

  PerformanceFlightSelectionPageRendered = 'performance_flight_selection_page_rendered',
  PerformanceFlightSelectionPageRenderedWithContent = 'performance_flight_selection_page_rendered_with_content',

  PerformanceFerrySelectionPageRendered = 'performance_ferry_selection_page_rendered',
  PerformanceFerrySelectionPageRenderedWithContent = 'performance_ferry_selection_page_rendered_with_content',
}

const metrics = getMetrics();
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const HISTOGRAM_THRESHOLDS = [50, 100, 200, 400, 800, 1600, 3200, 6400, 12800];
const performanceMetricLabels = {
  isCrawler:
    typeof navigator === 'undefined'
      ? 'false'
      : isCrawlerUserAgent(navigator.userAgent),
};

function trackPerformancePageLoaded(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformancePageLoaded,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceAppInitiated(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceAppInitiated,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceItineraryPricesRequested(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceItineraryPricesRequested,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceItineraryPricesReceived(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceItineraryPricesReceived,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceItineraryPricesRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceItineraryPricesRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceBuildYourTripPageRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceBuildYourTripPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceTripOverviewPageRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceTripOverviewPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceTripOverviewPageRenderedWithContent(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceTripOverviewPageRenderedWithContent,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelSelectionPageRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceHotelSelectionPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelSelectionPageRenderedWithContent(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceHotelSelectionPageRenderedWithContent,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelDetailsPageRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceHotelDetailsPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceHotelDetailsPageRenderedWithContent(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceHotelDetailsPageRenderedWithContent,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivitySelectionPageRendered(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceActivitySelectionPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivitySelectionPageRenderedWithContent(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceActivitySelectionPageRenderedWithContent,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivityDetailsPageRendered(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceActivityDetailsPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceActivityDetailsPageRenderedWithContent(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceActivityDetailsPageRenderedWithContent,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceFlightSelectionPageRendered(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceFlightSelectionPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceFlightSelectionPageRenderedWithContent(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceFlightSelectionPageRenderedWithContent,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceFerrySelectionPageRendered(timeToEvent: number): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceFerrySelectionPageRendered,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

function trackPerformanceFerrySelectionPageRenderedWithContent(
  timeToEvent: number
): void {
  const histogram = metrics.getHistogram(
    PerformanceMetricType.PerformanceFerrySelectionPageRenderedWithContent,
    HISTOGRAM_THRESHOLDS
  );
  histogram.observe(performanceMetricLabels, timeToEvent);
}

export function trackPerformance(): void {
  measureInitializationPerformance().then(
    ({ timeToAppInitiatedMs, timeToPageLoadedMs }) => {
      trackPerformancePageLoaded(timeToPageLoadedMs);
      trackPerformanceAppInitiated(timeToAppInitiatedMs);
    }
  );
  measureItineraryPricesPerformance().then(
    ({ timeToRequestMs, timeToReceiveMs, timeToRenderMs }) => {
      trackPerformanceItineraryPricesRequested(timeToRequestMs);
      trackPerformanceItineraryPricesReceived(timeToReceiveMs);
      trackPerformanceItineraryPricesRendered(timeToRenderMs);
    }
  );

  measureBuildYourTripPagePerformance().then(({ timeToRenderMs }) => {
    trackPerformanceBuildYourTripPageRendered(timeToRenderMs);
  });

  measureTripOverviewPagePerformance().then(
    ({ timeToRenderMs, timeToRenderWithContentMs }) => {
      trackPerformanceTripOverviewPageRendered(timeToRenderMs);
      trackPerformanceTripOverviewPageRenderedWithContent(
        timeToRenderWithContentMs
      );
    }
  );

  measureHotelSelectionPagePerformance().then(
    ({ timeToRenderMs, timeToRenderWithContentMs }) => {
      trackPerformanceHotelSelectionPageRendered(timeToRenderMs);
      trackPerformanceHotelSelectionPageRenderedWithContent(
        timeToRenderWithContentMs
      );
    }
  );

  measureHotelDetailsPagePerformance().then(
    ({ timeToRenderMs, timeToRenderWithContentMs }) => {
      trackPerformanceHotelDetailsPageRendered(timeToRenderMs);
      trackPerformanceHotelDetailsPageRenderedWithContent(
        timeToRenderWithContentMs
      );
    }
  );

  measureActivitySelectionPagePerformance().then(
    ({ timeToRenderMs, timeToRenderWithContentMs }) => {
      trackPerformanceActivitySelectionPageRendered(timeToRenderMs);
      trackPerformanceActivitySelectionPageRenderedWithContent(
        timeToRenderWithContentMs
      );
    }
  );

  measureActivityDetailsPagePerformance().then(
    ({ timeToRenderMs, timeToRenderWithContentMs }) => {
      trackPerformanceActivityDetailsPageRendered(timeToRenderMs);
      trackPerformanceActivityDetailsPageRenderedWithContent(
        timeToRenderWithContentMs
      );
    }
  );

  measureFlightSelectionPagePerformance().then(
    ({ timeToRenderMs, timeToRenderWithContentMs }) => {
      trackPerformanceFlightSelectionPageRendered(timeToRenderMs);
      trackPerformanceFlightSelectionPageRenderedWithContent(
        timeToRenderWithContentMs
      );
    }
  );

  measureFerrySelectionPagePerformance().then(
    ({ timeToRenderMs, timeToRenderWithContentMs }) => {
      trackPerformanceFerrySelectionPageRendered(timeToRenderMs);
      trackPerformanceFerrySelectionPageRenderedWithContent(
        timeToRenderWithContentMs
      );
    }
  );
}
